/**
 * TrialOnboardingGate — keeps an un-onboarded account on the guided path.
 *
 * Mounted once in AppContent (inside Router context). Two locked-in
 * populations (task #56 widened the second):
 *  - a signed-in ACTIVE trial whose staged flow isn't finished (trialPhase
 *    must reach 'done', not merely the profile save);
 *  - a code-less brand-new signup — no subscription doc, no org membership,
 *    not internal/admin — who belongs on /onboarding's activate/redeem
 *    screen instead of an account-less workspace.
 * Any workspace surface ('/', /desktop, /wallet, /integrations, …)
 * redirects to /onboarding — which also makes "sign out, come back later,
 * sign in" resume exactly where they left off, from any entry point.
 * Marketing pages stay reachable; activated accounts, org members, expired
 * trials, and mid-invite acceptances are never touched
 * (see src/hooks/trialOnboardingGate.ts for the pinned rules).
 *
 * The card step (2026-10-20, docs/PLAN-trial-card-on-file-20261020.md): once
 * onboarding is done, a trial that needs its card before any work sends its
 * owner or billing manager to /subscription/trial, onboarding's last step,
 * from the same app surfaces. One component decides both, so the two can
 * never send a person two ways at once: onboarding first.
 */

import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useTrialOnboardingGate } from '../../hooks/useTrialOnboardingGate';
import { useTrialCardGate } from '../../hooks/useTrialCardGate';
import { isGatedAppPath, trialCardPath } from '../../hooks/trialOnboardingGate';

export const TrialOnboardingGate: React.FC = () => {
  const { loading, needsOnboarding } = useTrialOnboardingGate();
  const card = useTrialCardGate();
  const location = useLocation();

  if (loading) return null;
  // App surfaces only — '/' and marketing pages are NEVER redirected
  // (2026-08-14 incident: hijacking the home page trapped the owner in a
  // bounce loop on mobile). On '/' the lock is the hidden chat widget.
  if (!isGatedAppPath(location.pathname)) return null;
  if (needsOnboarding) return <Navigate to="/onboarding" replace />;
  if (!card.loading && card.needsCard) return <Navigate to={trialCardPath(location.pathname, location.search)} replace />;
  return null;
};

export default TrialOnboardingGate;
